import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | 'guarantee' | null;
const modalContent: Record<Exclude<ModalType, null>, {
  title: string;
  body: string[];
}> = {
  terms: {
    title: 'Términos y Condiciones',
    body: ['Al adquirir el producto digital "Magnetismo Femenino: El Código de la Mujer de Élite", usted acepta los siguientes términos:', '1. Licencia de Uso: La compra otorga una licencia personal, intransferible y no exclusiva para acceder al contenido digital. Queda prohibida la reproducción, distribución, reventa o compartición del material sin autorización expresa.', '2. Entrega Digital: El producto se entrega de forma instantánea tras la confirmación del pago. Recibirá un enlace de descarga o acceso por correo electrónico.', '3. Propiedad Intelectual: Todo el contenido, incluyendo textos, gráficos, metodologías y técnicas descritas, es propiedad intelectual protegida por las leyes de derechos de autor.', '4. Responsabilidad: Los resultados pueden variar según la persona. El contenido es de carácter educativo e informativo y no constituye asesoramiento profesional psicológico ni médico.', '5. Modificaciones: Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones serán efectivas desde su publicación.', '6. Jurisdicción: Estos términos se rigen por las leyes aplicables en la jurisdicción del vendedor.']
  },
  privacy: {
    title: 'Política de Privacidad',
    body: ['Su privacidad es importante para nosotros. Esta política describe cómo recopilamos, usamos y protegemos su información personal.', '1. Información Recopilada: Recopilamos únicamente la información necesaria para procesar su compra: nombre, correo electrónico y datos de pago (procesados de forma segura por nuestro proveedor de pagos).', '2. Uso de la Información: Su información se utiliza exclusivamente para entregar el producto adquirido, enviar actualizaciones relacionadas con su compra y mejorar nuestros servicios.', '3. Protección de Datos: Implementamos medidas de seguridad estándar de la industria para proteger su información personal contra acceso no autorizado.', '4. No Compartimos su Información: No vendemos, alquilamos ni compartimos su información personal con terceros, excepto cuando sea necesario para procesar su pago.', '5. Cookies: Nuestro sitio puede utilizar cookies para mejorar su experiencia de navegación. Puede configurar su navegador para rechazar cookies.', '6. Contacto: Para cualquier consulta sobre privacidad, puede contactarnos a través de nuestros canales oficiales.']
  },
  guarantee: {
    title: 'Garantía de Devolución — 30 Días',
    body: ['Estamos tan seguros de que "Magnetismo Femenino: El Código de la Mujer de Élite" transformará tu presencia que ofrecemos una garantía completa de satisfacción.', 'Garantía de 30 Días: Si dentro de los primeros 30 días después de tu compra sientes que el contenido no cumplió con tus expectativas, te devolvemos el 100% de tu dinero. Sin preguntas, sin complicaciones.', 'Cómo Solicitar tu Devolución: Simplemente envía un correo electrónico a nuestro equipo de soporte indicando tu nombre y correo de compra. Procesaremos tu reembolso en un plazo de 5 a 7 días hábiles.', 'Condiciones: La garantía aplica únicamente para la primera compra del producto. El reembolso se realizará por el mismo método de pago utilizado en la compra original.', 'Nuestro Compromiso: Queremos que inviertas con total confianza. Si no ves un cambio en cómo el mundo reacciona a tu presencia, no queremos tu dinero.']
  }
};
function Modal({
  type,
  onClose
}: {
  type: Exclude<ModalType, null>;
  onClose: () => void;
}) {
  const content = modalContent[type];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-43">
      <motion.div initial={{
      opacity: 0,
      y: 30,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.3
    }} onClick={e => e.stopPropagation()} className="bg-darker border border-gold/20 rounded-sm w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" data-id="element-44">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gold/10" data-id="element-45">
          <div className="flex items-center gap-3" data-id="element-46">
            {type === 'guarantee' && <ShieldCheck className="w-5 h-5 text-gold" data-id="element-47" />}
            <h3 className="font-serif text-xl text-gold" data-id="element-48">{content.title}</h3>
          </div>
          <button onClick={onClose} className="text-cream/40 hover:text-cream transition-colors p-1" aria-label="Cerrar" data-id="element-49">
            <X className="w-5 h-5" data-id="element-50" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4" data-id="element-51">
          {content.body.map((paragraph, i) => <p key={i} className="font-sans text-sm text-cream/70 font-light leading-relaxed" data-id="element-52">
              {paragraph}
            </p>)}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gold/10" data-id="element-53">
          <button onClick={onClose} className="w-full py-3 bg-gold/10 hover:bg-gold/20 text-gold font-sans text-sm tracking-wider uppercase rounded-sm transition-colors" data-id="element-54">
            Entendido
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const year = new Date().getFullYear();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const links: {
    label: string;
    type: Exclude<ModalType, null>;
  }[] = [{
    label: 'Términos y Condiciones',
    type: 'terms'
  }, {
    label: 'Política de Privacidad',
    type: 'privacy'
  }, {
    label: 'Garantía de Devolución',
    type: 'guarantee'
  }];
  return <>
      <footer className="py-10 px-6 bg-darker border-t border-white/5" data-id="element-55">
        <div className="max-w-4xl mx-auto" data-id="element-56">
          {/* Links */}
          <div className="flex flex-wrap justify-center gap-6 mb-6" data-id="element-57">
            {links.map(link => <button key={link.type} onClick={() => setActiveModal(link.type)} className="font-sans text-xs text-cream/50 hover:text-gold transition-colors tracking-wide underline underline-offset-4 decoration-cream/20 hover:decoration-gold/50" data-id="element-58">
                {link.label}
              </button>)}
          </div>

          {/* Copyright */}
          <p className="font-sans text-xs text-cream/30 font-light text-center" data-id="element-59">
            &copy; {year} Magnetismo Femenino. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Modals */}
      <AnimatePresence data-id="element-60">
        {activeModal && <Modal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-61" />}
      </AnimatePresence>
    </>;
}